import { supabase } from '@/integrations/supabase/client';

export interface DomainVerification {
  id: string;
  workspace_id: string;
  domain: string;
  verification_token: string;
  verification_method: 'dns_txt' | 'dns_cname';
  status: 'pending' | 'verified' | 'failed' | 'expired';
  verified_at?: string;
  expires_at: string;
  created_at: string;
}

export interface DNSRecord {
  type: 'TXT' | 'CNAME';
  name: string;
  value: string;
  ttl: number;
}

export class DNSVerificationService {
  /**
   * Initiate domain verification for Premium/Enterprise workspaces
   */
  static async claimDomain(workspaceId: string, domain: string): Promise<DomainVerification | null> {
    try {
      // Check if workspace has Premium/Enterprise plan
      const { data: workspace } = await (supabase as any)
        .from('watt_workspaces')
        .select('plan_type')
        .eq('id', workspaceId)
        .single();

      if (!workspace || !['premium', 'enterprise'].includes(workspace.plan_type)) {
        throw new Error('Domain verification requires Premium or Enterprise plan');
      }

      // Check if domain is already claimed within this workspace
      const { data: existingClaims } = await (supabase as any)
        .from('domain_verifications')
        .select('*')
        .eq('workspace_id', workspaceId)
        .eq('domain', domain);

      if (existingClaims && existingClaims.length > 0) {
        const activeClaim = existingClaims.find((claim: any) => 
          claim.status === 'verified' || claim.status === 'pending'
        );
        if (activeClaim) {
          throw new Error(`Domain ${domain} is already ${activeClaim.status} for this workspace`);
        }
      }

      // Generate verification token
      const verificationToken = this.generateVerificationToken();
      const expiresAt = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000); // 7 days

      // Create domain verification record
      const { data, error } = await (supabase as any)
        .from('domain_verifications')
        .insert([{
          workspace_id: workspaceId,
          domain: domain,
          verification_token: verificationToken,
          verification_method: 'dns_txt',
          status: 'pending',
          expires_at: expiresAt.toISOString()
        }])
        .select()
        .single();

      if (error) throw error;

      return data;
    } catch (error) {
      console.error('Error claiming domain:', error);
      return null;
    }
  }

  /**
   * Get DNS records that need to be configured
   */
  static getDNSInstructions(domain: string, token: string): DNSRecord[] {
    return [
      {
        type: 'TXT',
        name: `_wattfactory-verify.${domain}`,
        value: token,
        ttl: 300
      }
    ];
  }

  /**
   * Verify DNS record exists
   */
  static async verifyDNSRecord(verificationId: string): Promise<boolean> {
    try {
      // Get verification record
      const { data: verification } = await (supabase as any)
        .from('domain_verifications')
        .select('*')
        .eq('id', verificationId)
        .single();

      if (!verification || verification.status !== 'pending') {
        return false;
      }

      // Check if expired
      if (new Date(verification.expires_at) < new Date()) {
        await this.updateVerificationStatus(verificationId, 'expired');
        return false;
      }

      // Verify DNS record using a DNS lookup service
      const isVerified = await this.checkDNSRecord(
        verification.domain, 
        verification.verification_token
      );

      if (isVerified) {
        await this.updateVerificationStatus(verificationId, 'verified');
        await this.enableCustomDomain(verification.workspace_id, verification.domain);
        return true;
      } else {
        return false;
      }
    } catch (error) {
      console.error('Error verifying DNS record:', error);
      await this.updateVerificationStatus(verificationId, 'failed');
      return false;
    }
  }

  /**
   * Check DNS TXT record using real DNS lookup
   */
  private static async checkDNSRecord(domain: string, expectedToken: string): Promise<boolean> {
    try {
      const txtRecord = `_wattfactory-verify.${domain}`;
      
      // Use Cloudflare's DNS-over-HTTPS API (works in browser)
      const dnsResponse = await fetch(
        `https://cloudflare-dns.com/dns-query?name=${txtRecord}&type=TXT`,
        {
          headers: {
            'Accept': 'application/dns-json'
          }
        }
      );

      if (!dnsResponse.ok) {
        console.error('DNS query failed:', dnsResponse.status);
        return false;
      }

      const dnsData = await dnsResponse.json();

      // Check if we have any TXT records
      if (!dnsData.Answer || dnsData.Answer.length === 0) {
        return false;
      }

      // Look for our verification token in the TXT records
      const txtRecords = dnsData.Answer
        .filter((record: any) => record.type === 16) // TXT records have type 16
        .map((record: any) => {
          // Remove quotes and clean up the TXT record data
          return record.data.replace(/"/g, '').trim();
        });

      // Check if any TXT record matches our expected token
      const isVerified = txtRecords.some((record: string) => 
        record === expectedToken
      );

      return isVerified;
    } catch (error) {
      console.error('DNS lookup error:', error);
      return false;
    }
  }

  /**
   * Update verification status
   */
  private static async updateVerificationStatus(
    verificationId: string, 
    status: 'verified' | 'failed' | 'expired'
  ): Promise<void> {
    const updateData: any = { status };
    
    if (status === 'verified') {
      updateData.verified_at = new Date().toISOString();
    }

    await (supabase as any)
      .from('domain_verifications')
      .update(updateData)
      .eq('id', verificationId);
  }

  /**
   * Enable custom domain for workspace
   */
  private static async enableCustomDomain(workspaceId: string, domain: string): Promise<void> {
    // Extract subdomain from the verified domain to update the workspace slug
    const newSlug = this.extractSubdomainFromDomain(domain);
    
    // Check if the new slug would conflict with existing workspaces
    if (newSlug) {
      const { data: existingWorkspace } = await (supabase as any)
        .from('watt_workspaces')
        .select('id')
        .eq('slug', newSlug)
        .neq('id', workspaceId)
        .single();

      // If slug conflicts, keep the original slug but still enable the domain
      if (existingWorkspace) {
        console.warn(`⚠️ Slug conflict: ${newSlug} already exists. Keeping original slug.`);
        await (supabase as any)
          .from('watt_workspaces')
          .update({ 
            domain: domain,
            domain_verified: true 
          })
          .eq('id', workspaceId);
      } else {
        // Update both domain and slug
        await (supabase as any)
          .from('watt_workspaces')
          .update({ 
            domain: domain,
            domain_verified: true,
            slug: newSlug
          })
          .eq('id', workspaceId);
          
        console.log(`✅ Updated workspace slug to: ${newSlug} for domain: ${domain}`);
      }
    } else {
      // If we can't extract a valid subdomain, just enable the domain
      await (supabase as any)
        .from('watt_workspaces')
        .update({ 
          domain: domain,
          domain_verified: true 
        })
        .eq('id', workspaceId);
    }
  }

  /**
   * Extract subdomain from a domain for use as workspace slug
   */
  private static extractSubdomainFromDomain(domain: string): string | null {
    try {
      // Handle different domain patterns:
      // test.wattfactory.tech -> test
      // manufacturing.tesla.com -> manufacturing
      // factory.apple.com -> factory
      
      const parts = domain.split('.');
      
      // Must have at least 3 parts (subdomain.domain.tld)
      if (parts.length < 3) {
        return null;
      }
      
      // Get the first part as the subdomain
      const subdomain = parts[0];
      
      // Validate subdomain format (alphanumeric, hyphens, 3-20 chars)
      if (!/^[a-z0-9-]{3,20}$/.test(subdomain)) {
        return null;
      }
      
      // Don't allow certain reserved words
      const reservedWords = ['www', 'api', 'admin', 'app', 'mail', 'ftp', 'cdn'];
      if (reservedWords.includes(subdomain)) {
        return null;
      }
      
      return subdomain;
    } catch (error) {
      console.error('Error extracting subdomain:', error);
      return null;
    }
  }

  /**
   * Generate verification token
   */
  private static generateVerificationToken(): string {
    return 'wf_' + Math.random().toString(36).substring(2, 15) + 
           Math.random().toString(36).substring(2, 15);
  }

  /**
   * Get workspace domain verifications
   */
  static async getWorkspaceDomainVerifications(workspaceId: string): Promise<DomainVerification[]> {
    try {
      const { data, error } = await (supabase as any)
        .from('domain_verifications')
        .select('*')
        .eq('workspace_id', workspaceId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching domain verifications:', error);
      return [];
    }
  }

  /**
   * Check if workspace can use custom domains
   */
  static async canUseCustomDomains(workspaceId: string): Promise<boolean> {
    try {
      const { data: workspace } = await (supabase as any)
        .from('watt_workspaces')
        .select('plan_type')
        .eq('id', workspaceId)
        .single();

      return workspace && ['premium', 'enterprise'].includes(workspace.plan_type);
    } catch (error) {
      console.error('Error checking domain permissions:', error);
      return false;
    }
  }
} 